import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ArrowUpRight, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';
import { IMG } from '../data/realImages';

// Build a flat list { src, category } from the manifest
function buildItems() {
  const groups = [
    { key: 'construction', items: IMG.construction || [] },
    { key: 'electrical',   items: IMG.electrical   || [] },
    { key: 'industrial',   items: IMG.industrial   || [] },
    { key: 'agriculture',  items: IMG.agriculture  || [] },
    { key: 'workforce',    items: IMG.workforce    || [] },
  ];
  const out = [];
  groups.forEach(({ key, items }) => {
    items.forEach((src, idx) => {
      out.push({ src, category: key, id: `${key}-${idx + 1}` });
    });
  });
  return out;
}

const CATEGORY_ORDER = ['all', 'construction', 'electrical', 'industrial', 'agriculture', 'workforce'];

export default function Gallery() {
  const { t } = useLang();
  const allItems = useMemo(() => buildItems(), []);
  const [active, setActive] = useState('all');
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const filtered = useMemo(
    () => (active === 'all' ? allItems : allItems.filter((i) => i.category === active)),
    [active, allItems]
  );

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const next = useCallback(
    () => setLightboxIdx((i) => (i + 1) % filtered.length),
    [filtered.length]
  );
  const prev = useCallback(
    () => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length),
    [filtered.length]
  );

  // Keyboard nav + lock body scroll while lightbox open
  useEffect(() => {
    if (lightboxIdx < 0) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxIdx, closeLightbox, next, prev]);

  // Reset lightbox if filter changes
  useEffect(() => { setLightboxIdx(-1); }, [active]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-40 border-t border-[#27272A]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-7">
            <Reveal className="section-index">{t('gallery.index')}</Reveal>
            <Reveal as="h2" className="h-section text-[#F5F5F2] mt-4 reveal-delay-1">
              {t('gallery.title')}
            </Reveal>
          </div>
          <Reveal className="md:col-span-5 md:pt-4 text-[#A1A1AA] text-sm md:text-base leading-relaxed reveal-delay-2">
            {t('gallery.sub')}
          </Reveal>
        </div>

        {/* Filters */}
        <Reveal className="reveal-delay-2 flex flex-wrap items-center gap-2 md:gap-3 mb-10">
          {CATEGORY_ORDER.map((c) => {
            const isActive = active === c;
            const count = c === 'all' ? allItems.length : allItems.filter((i) => i.category === c).length;
            return (
              <button
                key={c}
                data-testid={`gallery-filter-${c}`}
                onClick={() => setActive(c)}
                className={`group inline-flex items-center gap-2 px-4 py-2 border font-mono text-[10px] tracking-[0.22em] uppercase transition-colors ${
                  isActive
                    ? 'bg-[#C8102E] border-[#C8102E] text-white'
                    : 'border-[#27272A] text-[#A1A1AA] hover:border-[#C8102E] hover:text-white'
                }`}
              >
                <span>{t(`gallery.categories.${c}`)}</span>
                <span className={`text-[9px] ${isActive ? 'text-white/85' : 'text-[#52525B] group-hover:text-[#C8102E]'}`}>{count}</span>
              </button>
            );
          })}
          <span className="ml-auto hidden md:inline-flex font-mono text-[10px] tracking-[0.22em] uppercase text-[#71717A]">
            {filtered.length} {t('gallery.counter')}
          </span>
        </Reveal>

        {/* Masonry-style grid via CSS columns */}
        {filtered.length === 0 ? (
          <div className="py-24 text-center text-[#A1A1AA]">{t('gallery.empty')}</div>
        ) : (
          <div
            data-testid="gallery-grid"
            className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-5 [&>*]:mb-4 md:[&>*]:mb-5"
          >
            {filtered.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                data-testid={`gallery-tile-${idx}`}
                onClick={() => openLightbox(idx)}
                className="group relative block w-full overflow-hidden border border-[#27272A] bg-[#0F0F11] break-inside-avoid transition-colors hover:border-[#C8102E]"
              >
                <img
                  src={item.src}
                  alt={`${t(`gallery.categories.${item.category}`)} — ALPHA 7`}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/85 via-[#0A0A0B]/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="pointer-events-none absolute left-4 right-4 bottom-4 flex items-end justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#C8102E]">
                      {t(`gallery.categories.${item.category}`)}
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/85">{item.id.toUpperCase()}</div>
                  </div>
                  <span className="w-9 h-9 inline-flex items-center justify-center bg-[#C8102E] text-white">
                    <Maximize2 size={14} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIdx >= 0 && filtered[lightboxIdx] && (
        <div
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          className="fixed inset-0 z-[80] bg-[#050506]/95 backdrop-blur-sm flex flex-col"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 md:px-10 py-5 border-b border-[#1F1F22]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 bg-[#C8102E]" />
              <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#A1A1AA]">
                {t(`gallery.categories.${filtered[lightboxIdx].category}`)}
              </span>
              <span className="font-mono text-[11px] tracking-[0.24em] text-[#52525B]">
                {String(lightboxIdx + 1).padStart(2, '0')} / {String(filtered.length).padStart(2, '0')}
              </span>
            </div>
            <button
              data-testid="lightbox-close"
              aria-label={t('gallery.close')}
              onClick={closeLightbox}
              className="w-10 h-10 inline-flex items-center justify-center border border-[#27272A] text-[#F5F5F2] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Image area */}
          <div className="relative flex-1 flex items-center justify-center px-3 md:px-12 py-6" onClick={(e) => e.stopPropagation()}>
            <img
              key={filtered[lightboxIdx].src}
              src={filtered[lightboxIdx].src}
              alt="ALPHA 7 project"
              className="max-h-full max-w-full object-contain shadow-[0_30px_120px_rgba(0,0,0,0.6)]"
            />
            {filtered.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  aria-label={t('gallery.prev')}
                  onClick={prev}
                  className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center border border-[#27272A] bg-[#0A0A0B]/70 text-[#F5F5F2] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  data-testid="lightbox-next"
                  aria-label={t('gallery.next')}
                  onClick={next}
                  className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 inline-flex items-center justify-center border border-[#27272A] bg-[#0A0A0B]/70 text-[#F5F5F2] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>

          {/* Bottom meta */}
          <div className="px-5 md:px-10 py-4 border-t border-[#1F1F22] flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#71717A]">
              ALPHA 7 Soluciones y Proyectos · Project Archive
            </span>
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#71717A] hidden sm:inline-flex items-center gap-3">
              ESC · ← →
              <ArrowUpRight size={12} className="text-[#C8102E]" />
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
